import { TFunction } from 'i18next';
import { TableCellProps } from 'material-hu';
import { InfiniteListProps } from 'src/components/list/InfiniteList';
import { User } from 'src/types/user';
import { Pagination } from 'src/types/services';
import { Attachment } from 'src/types/attachments';

export enum VacationRequestSources {
  MY_TIME_OFF = 'MY_TIME_OFF',
  REQUEST_CREATION = 'REQUEST_CREATION',
  REQUEST_DETAIL = 'REQUEST_DETAIL'
}

export enum VacationActionsSources {
  MANAGEMENT_DETAIL = 'MANAGEMENT_DETAIL',
  MANAGEMENT_LIST = 'MANAGEMENT_LIST',
  COLLABORATOR_DETAIL = 'COLLABORATOR_DETAIL',
  COLLABORATOR_LIST = 'COLLABORATOR_LIST'
}

export enum ResponseCode {
  VACATION_APPROVER_NOT_AVAILABLE_FOR_USER = 'VACATION_APPROVER_NOT_AVAILABLE_FOR_USER',
  FROM_DATE_BEFORE_TODAY_NOT_ALLOWED = 'FROM_DATE_BEFORE_TODAY_NOT_ALLOWED',
  VACATION_REQUEST_NOT_PENDING = 'VACATION_REQUEST_NOT_PENDING',
  ERROR_MAX_ATTACHMENTS_SIZE_VACATIONS = 'ERROR_MAX_ATTACHMENTS_SIZE_VACATIONS'
}

export enum Role {
  MANAGER = 'MANAGER',
  COLLABORATOR = 'COLLABORATOR',
}

export enum OperationsBalance {
  ADDITION = 'ADDITION',
  SUBTRACTION = 'SUBTRACTION',
}

export enum UnitVacationes {
  DAYS = 'DAYS',
  HOURS = 'HOURS',
}

export enum AllowanceType {
  BASIC_ANNUAL = 'BASIC_ANNUAL',
  UNLIMITED = 'UNLIMITED',
}

export enum CountingMethod {
  CALENDAR_DAYS = 'CALENDAR_DAYS',
  BUSINESS_DAYS = 'BUSINESS_DAYS'
}

export enum StepStatus {
  PENDING = 'PENDING',
  WAITING_OTHER_STEP = 'WAITING_OTHER_STEP',
  REJECTED = 'REJECTED',
  APPROVED = 'APPROVED'
}

export enum VacationStatus {
  IN_PROGRESS = 'IN_PROGRESS',
  REJECTED = 'REJECTED',
  APPROVED = 'APPROVED',
  CANCELLED = 'CANCELLED'
}

export enum ReportType {
  BALANCES = 'balances',
  REQUESTS = 'requests',
}

export enum TabManagementType {
  MANAGEMENT_TAB = 'management_tab',
}

export enum BalanceConformationTypeEnum {
  BALANCE_REMNANT_CARRYOVER = 'BALANCE_REMNANT_CARRYOVER',
  BALANCE_USAGE = 'BALANCE_USAGE',
  BALANCE_USAGE_CORRECTION = 'BALANCE_USAGE_CORRECTION',
  CYCLE_ADDITION = 'CYCLE_ADDITION',
  CYCLE_CONCLUSION = 'CYCLE_CONCLUSION',
  BALANCE_MANUAL_CORRECTION = 'BALANCE_MANUAL_CORRECTION',
  BALANCE_BULK_CORRECTION = 'BALANCE_BULK_CORRECTION',
  POLICY_ALLOWANCE_TYPE_CHANGE = 'POLICY_ALLOWANCE_TYPE_CHANGE',
  POLICY_CYCLE_ALLOWANCE_AMOUNT_CHANGE = 'POLICY_CYCLE_ALLOWANCE_AMOUNT_CHANGE',
  USER_HIRING_DATE_CHANGE = 'USER_HIRING_DATE_CHANGE',
  USER_POLICY_CHANGE = 'USER_POLICY_CHANGE',
  BALANCE_USAGE_CANCELLATION = 'BALANCE_USAGE_CANCELLATION'
}

export enum AttachmentRequirement {
  NONE = 'NONE',
  OPTIONAL = 'OPTIONAL',
  REQUIRED = 'REQUIRED'
}

export type CollaboratorOrManagerData = Partial<InfiniteListProps> & {
  data?: Vacations,
  policyTypes?: PolicyTypes[],
  countRequestManager?: number,
  hasRequests?: boolean
};

export type ParamsDatesVacationCell = {
  date: string | Date;
  user: User;
  isUtc?: boolean
  type: string;
};

export type ParamsGetCorrectDays = {
  amount: number;
  operation: string;
  t?: TFunction;
  onlyNumbers?: boolean;
};

export type AdjustAvailableTimeForm = {
  operation: string;
  amount: number;
  observations: string;
};

export type AdjustAvailableTimeParams = {
  policyTypeId: number;
  userId: number;
  operation: string;
  amount: number;
  observations: string;
};

export type PolicyTypes = {
  id: number;
  name: string;
  description: string;
  attachmentInstructions?: string;
  attachmentRequirement?: AttachmentRequirement;
  icon?: string;
  allowanceType: AllowanceType;
  amount: number;
  unit: UnitVacationes;
  descriptionRequired: boolean;
  minimumAmountPerRequest?: number;
  maximumAmountPerRequest?: number;
  minimumBalance?: number;
  maximumBalance?: number;
  maximumRemnant?: number;
  countingMethod: CountingMethod;
  fromDateCycle: string,
  toDateCycle: string,
};

export type ResponseDetailPolicyTypes = {
  id: number,
  name: string,
  fromDateCycle: string,
  toDateCycle: string,
  unit: UnitVacationes,
  amountRequested: number,
  currentBalance: number,
  allowanceType: AllowanceType,
};

export type PolicyTypesBalance = {
  amount: number,
  eventType: string,
};

export type ResponseRequestTimeOff = {
  id: number;
  policyType: PolicyTypes;
  createdAt: string
  fromDate: string;
  toDate: string;
  state: VacationStatus;
  stepState: StepStatus | null;
  issuer?: User;
  approver?: User;
  description?: string;
  amountRequested?: number;
  amountAvailable?: number;
  canReview?: boolean;
  attachments: Attachment[];
};

export type ApprovalStep = {
  id: number;
  state: string;
  position: number;
  potentialApprovers: User[];
  potentialApproversCount: number;
};

export type VacationsNotificationsResponse = {
  vacationRequestsAsCollaboratorNotifications: number;
  vacationRequestsAsManagerNotifications: number;
};

export type TimeOffRequestedDaysResponse = {
  amount: number;
};

export type BalanceReport = {
  amountRequested: number,
  currentBalance?: number,
  policy: Partial<PolicyTypes> & {
    accumulationFrequency: string
    accumulationMoment: string
    allowanceAmount: number
    allowanceStart: string
    approvalRequired: boolean
    dismissalApportionmentMethod: string
    hiringApportionmentMethod: string
    peopleAmount: number,
    policyType: PolicyTypes
  },
  user: User,
};

export type ListTitleTableVacations = {
  id: string,
  text: string,
  align?: TableCellProps['align'],
};

export type BalanceConformationCustomData = {
  fromDate: string,
  toDate: string,
  observations: string | null,
  responsibleUserId: number | null,
  fromDateCycle: string,
  toDateCycle: string,
};

export type BalanceConformationType = {
  id: number,
  amount: number,
  operation: OperationsBalance,
  type: string,
  date: string,
  customData: Partial<BalanceConformationCustomData>
};

export type SitesInfo = {
  createdAt: string,
  id: number,
  body: string,
  title: string,
  updatedAt: string,
};

export type CalendarRequets = {
  user: User,
  site: SitesInfo,
  requests: ResponseRequestTimeOff[],
  holidays: HolidaysInfo[],
};

export type HolidaysInfo = {
  name: string;
  date: string;
  title: string;
};

export type ParamsGetWidthToRenderRequestInCalendar = {
  request: ResponseRequestTimeOff,
  differentsMonth?: boolean,
  amountDaysInMonth?: number
};

export type Vacations = ResponseRequestTimeOff[];
export type BalanceConfomationResponse = Pagination<BalanceConformationType>;
export type VacationsPaginated = Pagination<Vacations>;
export type PolicyTypesResponse = PolicyTypes[];
export type ResponsePolicyTypesBalance = PolicyTypesBalance[];
export type ResponseBalanceReport = Pagination<BalanceReport[]>;
export type ResponseRequestCalendar = Pagination<CalendarRequets[]>;
export type ApprovalStepsType = ApprovalStep[];
